import {
  Box,
  LinkProps,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material'
import { SxProps } from '@mui/system'
import { ElementType, ReactNode } from 'react'
import { SkeletonText, TextLink } from 'utoniq-ui'

import { Empty } from '@/components/pages/ArtistTop/template'
import { CellFrame } from '@/core/architecture'

type HorizontalListCellProps<I extends {}> = {
  loading?: boolean
  loadingItemLength?: number
  title: string
  titleTypographyProps?: TypographyProps
  subTitle?: string
  subTitleTypographyProps?: TypographyProps
  TitleLoadingComponent?: ReactNode
  TitleIcon?: ReactNode
  listHref?: string
  items: I[]
  renderItem: (item: I, i: number) => ReactNode
  LoadingComponent?: ElementType
  sx?: SxProps
  spacing?: number
  trans?: string
  transTypographyProps?: LinkProps
  EmptyComponent?: ReactNode
  allwayShowList?: boolean
}

export const HorizontalList = <I extends {}>({
  loading,
  loadingItemLength = 5,
  title,
  titleTypographyProps,
  subTitle,
  subTitleTypographyProps,
  TitleLoadingComponent,
  TitleIcon,
  listHref,
  items,
  renderItem,
  LoadingComponent = Box,
  sx,
  spacing = 5,
  trans,
  transTypographyProps,
  EmptyComponent = <Empty />,
  allwayShowList,
}: HorizontalListCellProps<I>) => {
  return (
    <CellFrame sx={{ position: 'relative', ...sx }}>
      <Box sx={{ pb: 6, pt: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ py: 2, px: 5 }}
        >
          {TitleLoadingComponent ? (
            loading && !items?.length ? (
              TitleLoadingComponent
            ) : (
              <Typography variant="h3" {...titleTypographyProps}>
                {title}
              </Typography>
            )
          ) : null}

          {!TitleLoadingComponent &&
            (loading ? (
              <SkeletonText variant="large" width="50%" />
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                {TitleIcon}
                <Typography variant="h3" {...titleTypographyProps}>
                  {title}
                </Typography>
              </Stack>
            ))}
          {!!listHref && loading && !allwayShowList && (
            <SkeletonText variant="large" width="10%" />
          )}
          {listHref && (!!items?.length || allwayShowList) && (
            <TextLink
              variant="h5"
              underline="none"
              sx={{ color: 'primary.main' }}
              href={listHref}
              {...transTypographyProps}
            >
              {trans}
            </TextLink>
          )}
        </Stack>
        {!!subTitle && (
          <Stack sx={{ px: 5 }}>
            <Typography variant="caption" {...subTitleTypographyProps}>
              {subTitle}
            </Typography>
          </Stack>
        )}
        {!!(loading || items?.length) && (
          <Stack
            flexWrap="wrap"
            sx={
              {
                overflowY: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '::-webkit-scrollbar': {
                  height: 0,
                  width: 0,
                  display: 'none',
                  MozAppearance: 'none',
                  WebkitAppearance: 'none',
                },
              } as SxProps
            }
          >
            <Stack
              className="items"
              direction="row"
              spacing={spacing}
              flexWrap="nowrap"
              overflow="hidden"
              sx={{
                pt: 3,
                pb: 6,
                '& > *:first-of-type': {
                  ml: 5,
                },
                '& > *:last-child': {
                  mr: 5,
                },
              }}
            >
              {loading
                ? Array.from(Array(loadingItemLength).keys()).map(i => (
                    <LoadingComponent key={i} />
                  ))
                : items?.map(renderItem)}
            </Stack>
          </Stack>
        )}
        {!loading && !items?.length && EmptyComponent}
      </Box>
    </CellFrame>
  )
}
